import React, { useMemo, useState } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { FaRobot, FaPaperPlane, FaTimes, FaSpinner } from "react-icons/fa";
import { rootUrl } from "../helpers";
import { useAuth } from "../context/auth-context";

export default function InAppCopilot() {
  const auth = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content: "Need help with this page? Ask me how to complete a task.",
    },
  ]);

  const canSend = useMemo(() => prompt.trim().length > 0 && !loading, [prompt, loading]);

  const send = async () => {
    if (!canSend) return;
    const q = prompt.trim();
    const next = [...messages, { role: "user", content: q }];
    setMessages(next);
    setPrompt("");
    setLoading(true);
    try {
      const res = await axios.post(rootUrl("/aiInAppHelp"), {
        cid: auth.user?.cid,
        uid: auth.user?.id,
        path: location.pathname,
        question: q,
        history: next.slice(-6),
      });
      const reply = res?.data?.data?.reply || "I could not generate help right now.";
      setMessages((prev) => [...prev, { role: "assistant", content: reply }]);
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.data?.error?.message ||
        (typeof e?.response?.data === "string" ? e.response.data : "") ||
        e?.message ||
        "Help service unavailable.";
      setMessages((prev) => [...prev, { role: "assistant", content: msg }]);
    } finally {
      setLoading(false);
    }
  };

  // Reports page already has its own AI assistant
  if (location.pathname === "/reports") return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed right-6 bottom-6 z-40 bg-blue-950 hover:bg-blue-900 text-white rounded-full shadow-xl px-4 py-3 flex items-center gap-2"
      >
        <FaRobot />
        <span className="hidden sm:inline text-sm font-medium">Help</span>
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-black/30" onClick={() => setOpen(false)} />
          <div className="relative h-full w-full sm:w-[520px] bg-white border-l border-gray-200 shadow-2xl flex flex-col">
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
              <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                <FaRobot className="text-blue-900" /> In-App Copilot
              </h3>
              <button onClick={() => setOpen(false)} className="p-2 rounded hover:bg-gray-100">
                <FaTimes />
              </button>
            </div>

            <div className="flex-1 overflow-auto p-4 space-y-3 bg-gray-50">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] rounded-xl px-3 py-2 text-sm ${m.role === "user" ? "bg-blue-950 text-white" : "bg-white border border-gray-200 text-gray-800"}`}>
                    <pre className={`whitespace-pre-wrap font-sans ${m.role === "user" ? "text-white" : "text-gray-800"}`}>{m.content}</pre>
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-white border border-gray-200 text-gray-700 rounded-xl px-3 py-2 text-sm flex items-center gap-2">
                    <FaSpinner className="animate-spin" /> Thinking...
                  </div>
                </div>
              )}
            </div>

            <div className="p-3 border-t border-gray-200 bg-white flex items-end gap-2">
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                rows={2}
                placeholder="Ask how to do something on this page..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                type="button"
                onClick={send}
                disabled={!canSend}
                className="bg-blue-950 text-white rounded-lg px-3 py-2 disabled:opacity-60"
              >
                <FaPaperPlane />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
